import { useState, useRef, useCallback, useEffect } from 'react';
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import type { ExamLanguage } from '@/types/exam-config.types';
import { normalizeExamLanguage } from '@/utils/locale';

// Curated neural voices per language
const VOICE_MAP: Record<string, string> = {
  EN: 'en-US-JennyNeural',
  FR: 'fr-FR-DeniseNeural',
};

interface UseAzureTTSOptions {
  interviewId: string;
  language: ExamLanguage | string;
  apiBaseUrl: string;
}

interface UseAzureTTSReturn {
  speak: (text: string) => void;
  stop: () => void;
  isPlaying: boolean;
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
  isReady: boolean; // true once first token is fetched
  ttsError: string | null; // non-null when TTS is unavailable
}

interface TTSTokenResponse {
  token: string;
  region: string;
}

export function useAzureTTS({
  interviewId,
  language,
  apiBaseUrl,
}: UseAzureTTSOptions): UseAzureTTSReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [ttsError, setTtsError] = useState<string | null>(null);

  // Ref mirrors isMuted so speak() always reads the latest value (not a stale closure)
  const isMutedRef = useRef(false);

  // Token management refs — avoid re-renders on token updates
  const authTokenRef = useRef<string | null>(null);
  const regionRef = useRef<string | null>(null);
  const tokenIssuedAtRef = useRef<number>(0);

  // Synthesizer + audio player refs — player supports pause() for instant mute
  const synthesizerRef = useRef<sdk.SpeechSynthesizer | null>(null);
  const playerRef = useRef<sdk.SpeakerAudioDestination | null>(null);

  // Token refresh threshold: 9 minutes (540_000ms) — Azure tokens expire at 10 minutes
  const TOKEN_REFRESH_MS = 540_000;

  /**
   * Fetch a fresh TTS token from the backend.
   * Stores token + region + issuedAt timestamp in refs.
   * Returns true on success, false on failure.
   */
  const fetchToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/interviews/${interviewId}/tts-token`,
        { method: 'GET' }
      );
      if (!response.ok) {
        console.warn(`[useAzureTTS] Token fetch failed: HTTP ${response.status}`);
        return false;
      }
      const data: TTSTokenResponse = await response.json();
      authTokenRef.current = data.token;
      regionRef.current = data.region;
      tokenIssuedAtRef.current = Date.now();
      return true;
    } catch (err) {
      console.error('[useAzureTTS] Token fetch error:', err);
      return false;
    }
  }, [interviewId, apiBaseUrl]);

  /**
   * Returns a valid token, refreshing if necessary.
   * Returns null if token cannot be obtained (TTS will silently fail).
   */
  const getValidToken = useCallback(async (): Promise<{ token: string; region: string } | null> => {
    const age = Date.now() - tokenIssuedAtRef.current;
    const needsRefresh = !authTokenRef.current || age >= TOKEN_REFRESH_MS;

    if (needsRefresh) {
      const success = await fetchToken();
      if (!success) return null;
    }

    if (!authTokenRef.current || !regionRef.current) return null;
    return { token: authTokenRef.current, region: regionRef.current };
  }, [fetchToken]);

  /**
   * Stop any active synthesizer and reset playing state.
   */
  const stop = useCallback(() => {
    // Pause the audio player first — instant silence
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.close();
      } catch (err) {
        console.warn('[useAzureTTS] Error stopping player:', err);
      }
      playerRef.current = null;
    }
    if (synthesizerRef.current) {
      try {
        synthesizerRef.current.close();
      } catch (err) {
        console.warn('[useAzureTTS] Error closing synthesizer:', err);
      }
      synthesizerRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  /**
   * Speak the provided text using Azure Neural TTS.
   * - No-op if muted or token unavailable.
   * - Stops any current speech before starting new synthesis.
   * - Timer runs DURING TTS playback — this hook does not interact with the timer.
   */
  const speak = useCallback(
    async (text: string) => {
      // Check ref (not closure state) so even stale speak references respect mute
      if (isMutedRef.current) {
        console.log('[useAzureTTS] speak() blocked — muted');
        return;
      }
      if (!text || !text.trim()) return;

      // Get (or refresh) token
      const tokenData = await getValidToken();
      if (!tokenData) {
        console.warn('[useAzureTTS] No valid token available — TTS silently skipped');
        return;
      }

      // Stop any currently playing speech
      stop();

      try {
        const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
          tokenData.token,
          tokenData.region
        );

        // Select voice based on exam language (`EN`/`FR`) — accepts `en`/`fr` too
        const voiceName = VOICE_MAP[normalizeExamLanguage(language)] || VOICE_MAP.EN;
        speechConfig.speechSynthesisVoiceName = voiceName;

        const player = new sdk.SpeakerAudioDestination();
        playerRef.current = player;
        const audioConfig = sdk.AudioConfig.fromSpeakerOutput(player);
        const synthesizer = new sdk.SpeechSynthesizer(speechConfig, audioConfig);
        synthesizerRef.current = synthesizer;

        setIsPlaying(true);

        synthesizer.speakTextAsync(
          text,
          (result) => {
            if (result.reason === sdk.ResultReason.SynthesizingAudioCompleted) {
              console.log('[useAzureTTS] Speech synthesis completed');
            } else {
              console.warn('[useAzureTTS] Speech synthesis result:', result.reason, result.errorDetails);
            }
            synthesizer.close();
            if (synthesizerRef.current === synthesizer) {
              synthesizerRef.current = null;
            }
            setIsPlaying(false);
          },
          (err) => {
            console.error('[useAzureTTS] Speech synthesis error:', err);
            synthesizer.close();
            if (synthesizerRef.current === synthesizer) {
              synthesizerRef.current = null;
            }
            setIsPlaying(false);
          }
        );
      } catch (err) {
        // Graceful failure — never crash the interview
        console.error('[useAzureTTS] Failed to initialize synthesizer:', err);
        setIsPlaying(false);
      }
    },
    [language, getValidToken, stop]
  );

  // Eager token fetch on mount — pre-warm so first speak() call is fast
  useEffect(() => {
    if (!interviewId) return;

    fetchToken().then((success) => {
      if (success) {
        setIsReady(true);
        setTtsError(null);
        console.log('[useAzureTTS] TTS token pre-warmed successfully');
      } else {
        console.warn('[useAzureTTS] TTS token pre-warm failed — TTS unavailable');
        setIsReady(true); // Don't block UI
        setTtsError('Voice narration unavailable — check Azure Speech configuration');
      }
    });
  }, [interviewId, fetchToken]);

  // Keep ref in sync with state — speak() reads the ref for real-time mute check
  useEffect(() => {
    isMutedRef.current = isMuted;
    if (isMuted) {
      console.log('[useAzureTTS] Muted — stopping any active speech');
      stop();
    }
  }, [isMuted, stop]);

  // Cleanup on unmount — release any active synthesizer
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    speak,
    stop,
    isPlaying,
    isMuted,
    setIsMuted,
    isReady,
    ttsError,
  };
}
